import { CSSProperties, FC, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  PlacesType,
  Tooltip as ReactTooltip,
  VariantType,
} from 'react-tooltip';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';

const DEFAULT_MAX_WIDTH = 220;
const DEFAULT_Z_INDEX = 100;

const buildTooltipStyle = (
  maxWidth: number | string | undefined,
  zIndex: number | undefined
): CSSProperties => ({
  maxWidth:
    maxWidth === undefined
      ? `${DEFAULT_MAX_WIDTH}px`
      : typeof maxWidth === 'number'
        ? `${maxWidth}px`
        : maxWidth,
  zIndex: zIndex ?? DEFAULT_Z_INDEX,
});

interface TooltipProps {
  id: string;
  children: ReactNode;
  maxWidth?: number | string;
  zIndex?: number;
  place?: PlacesType;
  className?: string;
}

export const Tooltip: FC<TooltipProps> = ({
  id,
  children,
  maxWidth,
  zIndex,
  place,
  className,
}) =>
  createPortal(
    <ReactTooltip
      id={id}
      style={buildTooltipStyle(maxWidth, zIndex)}
      place={place}
      className={className}
    >
      {children}
    </ReactTooltip>,
    document.body
  );

interface IconTooltipProps {
  id: string;
  children: ReactNode;
  iconPath?: string;
  iconSize?: number;
  iconClassName?: string;
  wrapperClassName?: string;
  maxWidth?: number | string;
  zIndex?: number;
  place?: PlacesType;
  variant?: VariantType;
  tooltipClassName?: string;
  noArrow?: boolean;
  opacity?: CSSProperties['opacity'];
  onMouseEnter?: () => void;
}

export const IconTooltip: FC<IconTooltipProps> = ({
  id,
  children,
  iconPath = ICONS.tooltip,
  iconSize = 20,
  iconClassName,
  wrapperClassName,
  maxWidth,
  zIndex,
  place,
  variant = 'dark',
  tooltipClassName,
  noArrow = false,
  opacity,
  onMouseEnter,
}) => (
  <>
    <div
      className={wrapperClassName}
      data-tooltip-id={id}
      onMouseEnter={onMouseEnter}
    >
      <Icon pathToIcon={iconPath} size={iconSize} className={iconClassName} />
    </div>
    {createPortal(
      <ReactTooltip
        id={id}
        style={buildTooltipStyle(maxWidth, zIndex)}
        place={place}
        variant={variant}
        className={tooltipClassName}
        noArrow={noArrow}
        opacity={opacity}
      >
        {children}
      </ReactTooltip>,
      document.body
    )}
  </>
);
